<script setup lang="ts">
/**
 * HzButton
 *
 * Calibration source: 02-design-system/composants.dc.html section "HzButton"
 * and extracted/notes-maquette.md ("Bandeau client", "Bloc aide bas").
 *
 * Geometry:
 * - primary / secondary: height 36px, radius 8px, font 13px / 600
 * - compact: height 28px, radius pill, font 11.5px / 600 (help block "Contacter")
 * - size sm  : height 32px, font 12px    (empty and error states of cards)
 * - size md  : height 36px, font 13px    (default, contract value)
 * - size lg  : height 38px, radius 10px  (client banner action row)
 *
 * The `compact` variant owns its own geometry and ignores `size`.
 * Every colour is read from a --hz-* token. White foreground on a primary
 * surface uses the --hz-bg-card token.
 */
import { computed } from 'vue';

type ButtonVariant = 'primary' | 'secondary' | 'compact';
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonType = 'button' | 'submit' | 'reset';

const props = withDefaults(
  defineProps<{
    /** Visual variant. Only one primary button per zone (design system rule). */
    variant?: ButtonVariant;
    /** Height and typography scale. Ignored when variant is "compact". */
    size?: ButtonSize;
    /** Native button type. Defaults to "button" to avoid accidental submits. */
    type?: ButtonType;
    /** Disables interaction and applies the reduced-opacity resting style. */
    disabled?: boolean;
    /** Shows the spinner, blocks interaction and exposes aria-busy. */
    loading?: boolean;
    /** Stretches the button to the full width of its container. */
    block?: boolean;
    /** Accessible name when the button carries an icon only. */
    ariaLabel?: string;
  }>(),
  {
    variant: 'primary',
    size: 'md',
    type: 'button',
    disabled: false,
    loading: false,
    block: false,
    ariaLabel: '',
  },
);

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const isInert = computed<boolean>(() => props.disabled || props.loading);

const classes = computed<Record<string, boolean>>(() => ({
  'hz-btn--primary': props.variant === 'primary',
  'hz-btn--secondary': props.variant === 'secondary',
  'hz-btn--compact': props.variant === 'compact',
  'hz-btn--sm': props.variant !== 'compact' && props.size === 'sm',
  'hz-btn--md': props.variant !== 'compact' && props.size === 'md',
  'hz-btn--lg': props.variant !== 'compact' && props.size === 'lg',
  'hz-btn--block': props.block,
  'hz-btn--loading': props.loading,
}));

function onClick(event: MouseEvent): void {
  if (isInert.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  emit('click', event);
}
</script>

<template>
  <button
    class="hz-btn"
    :class="classes"
    :type="props.type"
    :disabled="isInert"
    :aria-busy="props.loading ? 'true' : undefined"
    :aria-label="props.ariaLabel === '' ? undefined : props.ariaLabel"
    @click="onClick"
  >
    <span
      v-if="props.loading"
      class="hz-btn__spinner"
      aria-hidden="true"
    />
    <slot />
  </button>
</template>

<style scoped>
.hz-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--hz-sp-2);
  font-family: var(--hz-font);
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  border: 1px solid transparent;
  border-radius: var(--hz-radius);
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease;
}

.hz-btn:focus-visible {
  outline: none;
  box-shadow: var(--hz-focus);
}

.hz-btn:disabled {
  cursor: not-allowed;
}

.hz-btn--block {
  width: 100%;
}

/* Size scale, contract value is md. */
.hz-btn--sm {
  height: 32px;
  padding: 0 14px;
  font-size: 12px;
}

.hz-btn--md {
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
}

.hz-btn--lg {
  height: 38px;
  padding: 0 16px;
  font-size: 13px;
  border-radius: 10px;
}

/* Primary: primary-900 at rest, primary-700 on hover. */
.hz-btn--primary {
  background: var(--hz-primary-900);
  color: var(--hz-bg-card);
  border-color: var(--hz-primary-900);
}

.hz-btn--primary:hover:not(:disabled) {
  background: var(--hz-primary-700);
  border-color: var(--hz-primary-700);
}

.hz-btn--primary:disabled {
  opacity: 0.45;
}

/* Secondary: card background, --hz-border outline, app background on hover. */
.hz-btn--secondary {
  background: var(--hz-bg-card);
  color: var(--hz-text-strong);
  border-color: var(--hz-border);
}

.hz-btn--secondary:hover:not(:disabled) {
  background: var(--hz-bg-app);
}

.hz-btn--secondary:disabled {
  color: var(--hz-text-muted);
  opacity: 0.5;
}

/* Compact: pill, height 28px, own typography. */
.hz-btn--compact {
  height: 28px;
  padding: 0 16px;
  font-size: 11.5px;
  border-radius: var(--hz-radius-pill);
  background: var(--hz-primary-900);
  color: var(--hz-bg-card);
  border-color: var(--hz-primary-900);
}

.hz-btn--compact:hover:not(:disabled) {
  background: var(--hz-primary-700);
  border-color: var(--hz-primary-700);
}

.hz-btn--compact:disabled {
  opacity: 0.45;
}

/* Loading keeps the resting colours and only lowers the pointer affordance. */
.hz-btn--loading {
  cursor: progress;
}

.hz-btn__spinner {
  flex: none;
  width: 13px;
  height: 13px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: var(--hz-radius-pill);
  opacity: 0.75;
  /* hz-spin is declared globally in styles/tokens.css. */
  animation: hz-spin 0.8s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .hz-btn__spinner {
    animation-duration: 2.4s;
  }
}
</style>
